import React, {useEffect, useState} from 'react';
import {StyleSheet, View, Pressable} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import Spacer from '@/Atoms/Spacer';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import useArenaStore from '@/Store/ArenaStore';
import DotLoader from '../DotLoader';
import PlayerAvatar from '@/Screens/Leaderboard/components/PlayerAvatar';
import {
  lbColors,
  lbRadius,
  lbShadow,
} from '@/Screens/Leaderboard/leaderboardTheme';
import {initialsForName} from '@/Screens/Leaderboard/useLeaderboardData';
import useGetArenaPlayerGames, {
  ArenaPlayerGame,
} from '../hooks/useGetArenaPlayerGames';
import ArenaPlayerGames from './ArenaPlayerGames';

// ── ArenaLobby ───────────────────────────────────────────────────────────────
// The "Arena" tab: a lean personal-status surface (the leaderboard now lives in
// its own tab, ArenaLeaderboard). Themed to match the wins-leaderboard look
// (leaderboardTheme tokens + PlayerAvatar): parchment background, a dark
// "YOUR SCORE" panel with your streak / recent-results chips, and a light state
// strip that tells you exactly what's happening (in-game / pooled / paused /
// finishing). Also:
//   - expand "Your results" → your full game history (GET /arenaPlayerGames)
//   - tap any of your games → opens its replay (redis-first, DB-uuid fallback)
//     via the openArenaGame callback threaded down from PlayChess.
// Socket ownership stays in PlayChess; this component only reads ArenaStore,
// calls onPause/onResume, and fetches history over REST.

const FIRE_COLOR = '#FF6B00';

function formatHMS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(
    seconds,
  ).padStart(2, '0')}`;
}

const FireIcon: React.FC<{size?: number}> = ({size = 16}) => (
  <MaterialCommunityIcons name="fire" size={size} color={FIRE_COLOR} />
);

// Per-game points chip style (frozen value contract). Rendered on the dark
// score panel: big wins are FILLED (on-fire orange / berserk gold), everything
// else is an outlined chip. 0=loss, 1=draw, 2=win, 3=berserk win, ≥4=on-fire.
const DRAW_BLUE = '#7FA8D9';
const scoreChipStyle = (
  v: number,
): {bg: string; fg: string; border: string} => {
  if (v >= 4) {
    return {bg: FIRE_COLOR, fg: '#FFFFFF', border: FIRE_COLOR};
  }
  if (v === 3) {
    return {bg: lbColors.gold, fg: lbColors.goldInk, border: lbColors.gold};
  }
  if (v === 2) {
    return {bg: 'transparent', fg: '#F0C24A', border: '#B98A1E'};
  }
  if (v === 1) {
    return {bg: 'transparent', fg: DRAW_BLUE, border: '#4A6A94'};
  }
  return {bg: 'transparent', fg: lbColors.darkText, border: '#5A4D2E'};
};

// Chronological (oldest→newest) per-game points as coloured digit chips;
// most-recent sits on the right. `max` keeps only the last N.
const ScoreChips: React.FC<{scores: number[]; max?: number}> = ({
  scores,
  max,
}) => {
  const shown = max ? scores.slice(-max) : scores;
  return (
    <Box style={styles.chipRow}>
      {shown.map((v, i) => {
        const c = scoreChipStyle(v);
        return (
          <Box
            key={i}
            style={[
              styles.scoreChip,
              {backgroundColor: c.bg, borderColor: c.border},
            ]}
            data-testid="arena-score-chip">
            <CCText
              fontSize={FontSizes[13]}
              fontWeight="bold"
              style={{color: c.fg}}>
              {v}
            </CCText>
          </Box>
        );
      })}
    </Box>
  );
};

const ArenaLobby: React.FC<{
  loggedInUserId: string;
  // The caller's display name — used for the "YOUR SCORE" avatar initials
  // BEFORE the first game, when the caller isn't yet in the shipped standings
  // (myEntry is undefined then, so initialsForName(undefined) would show "?").
  myName?: string;
  onPause: () => void;
  onResume: () => void;
  // tournament start (ms epoch) from details — drives the pre-start countdown
  // because arena_status_response carries endsAtMs only once running.
  startDateMs?: number;
  tournamentId?: string | number;
  // Same spectate plumbing games.tsx rows use — provided by PlayChess via
  // TournamentPlay.
  watchTournamentGame?: (
    pairingKey: string,
    tournamentId: string | number,
    round: number,
  ) => void;
  // A player who is mid-game must not spectate-jump (mirrors the !playing
  // guard on games.tsx row presses).
  playing?: boolean;
  // Opens a finished arena game's replay on the board (redis-first by tableId,
  // DB-uuid fallback) — threaded from PlayChess.openArenaGameByTable.
  openArenaGame?: (tableId: string, fallbackUuid?: string) => void;
}> = ({
  loggedInUserId,
  myName,
  onPause,
  onResume,
  startDateMs,
  tournamentId,
  playing,
  openArenaGame,
}) => {
  const status = useArenaStore(state => state.status);
  const standings = useArenaStore(state => state.standings);

  // Self-contained 1s ticker (independent of TournamentPlay's countdown
  // engine, which stays untouched for Swiss).
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // ── my game history ("View all games" under YOUR SCORE) ─────────────────
  const myGamesHook = useGetArenaPlayerGames();
  const [myGamesOpen, setMyGamesOpen] = useState(false);

  const openGameOnBoard = (game: ArenaPlayerGame) => {
    if (playing) {
      return; // don't jump off your live board
    }
    openArenaGame?.(game.tableId, game.gameUuid);
  };

  const toggleMyGames = () => {
    if (myGamesOpen) {
      setMyGamesOpen(false);
      myGamesHook.clear();
    } else {
      setMyGamesOpen(true);
      myGamesHook.fetchPlayerGames(tournamentId, loggedInUserId);
    }
  };

  const arenaState = status?.state || standings?.state || '';
  const endsAtMs = status?.endsAtMs || standings?.endsAtMs || 0;
  const finished = arenaState === 'finished';
  const finishing =
    arenaState === 'finishing' ||
    arenaState === 'finalizing' ||
    (!finished && endsAtMs > 0 && endsAtMs <= now);
  const notStarted = arenaState === 'created' || (!arenaState && !endsAtMs);

  let countdownLabel = '';
  if (finished) {
    countdownLabel = 'Finished';
  } else if (finishing) {
    countdownLabel = 'Finishing — waiting for the last games';
  } else if (notStarted && startDateMs && startDateMs > now) {
    countdownLabel = `Starts in ${formatHMS((startDateMs - now) / 1000)}`;
  } else if (endsAtMs > now) {
    countdownLabel = `Ends in ${formatHMS((endsAtMs - now) / 1000)}`;
  } else if (notStarted) {
    countdownLabel = 'Starting soon';
  }

  const myState = status?.myState || 'idle';
  // LOCAL truth first: `playing` (the live-board flag PlayChess owns) always
  // wins over a stale/pending myState so "Finding your next game…" can never
  // show while the board is live.
  const inGame = Boolean(playing) || myState === 'paired';
  const isPaused = myState === 'paused' || Boolean(status?.paused);
  const isJoined = useArenaStore(state => state.isJoined);
  const setStatus = useArenaStore(state => state.setStatus);
  const handlePause = () => {
    setStatus({paused: true});
    onPause?.();
  };
  const handleResume = () => {
    setStatus({paused: false});
    onResume?.();
  };
  const entries = standings?.entries || [];
  const myEntry = entries.find(e => String(e.uid) === String(loggedInUserId));
  const myRank = myEntry?.rank;
  const myPoints = status?.points ?? myEntry?.points ?? 0;
  const myStreak = status?.streak ?? myEntry?.streak ?? 0;
  const myOnFire = Boolean(status?.onFire || myEntry?.onFire);
  const myGamesPlayed = myEntry?.gamesPlayed ?? 0;

  // ── my state line + action (truthful, one pair per state) ───────────────
  // precedence: finished > finishing > in-game > paused > pooled > idle.
  // Colours are chosen to read on the LIGHT state strip (not the dark panel).
  let stateLine: React.ReactNode = null;
  let actionButton: React.ReactNode = null;

  if (finished || finishing) {
    stateLine = (
      <CCText
        fontSize={FontSizes[14]}
        fontWeight="semibold"
        style={{color: lbColors.ink}}>
        {finished
          ? myRank
            ? `Arena finished — you placed #${myRank}`
            : 'Arena finished'
          : inGame
          ? 'Games are finishing — no new pairings'
          : 'Arena is ending — no new games will start'}
      </CCText>
    );
  } else if (inGame) {
    stateLine = playing ? (
      <CCText
        fontSize={FontSizes[14]}
        fontWeight="semibold"
        style={{color: lbColors.ink}}>
        ♟ Game in progress — the board is live
      </CCText>
    ) : (
      <Box flexDirection="row" alignItems="center" gap={6}>
        <CCText
          fontSize={FontSizes[14]}
          fontWeight="semibold"
          style={{color: lbColors.ink}}>
          Game starting…
        </CCText>
        <DotLoader />
      </Box>
    );
    actionButton = isPaused ? (
      <CCText
        fontSize={FontSizes[12]}
        fontWeight="semibold"
        color={CCColors?.Red}
        data-testid="arena-paused-after-game">
        Paused after this game
      </CCText>
    ) : (
      <CCButton
        onPress={handlePause}
        style={styles.softButton}
        contentContainerStyle={styles.actionButtonContent}>
        <CCText
          fontSize={FontSizes[13]}
          fontWeight="semibold"
          style={{color: lbColors.goldInk}}>
          Pause after this game
        </CCText>
      </CCButton>
    );
  } else if (isPaused) {
    stateLine = (
      <CCText
        fontSize={FontSizes[14]}
        fontWeight="semibold"
        color={CCColors?.Red}>
        Paused
      </CCText>
    );
    actionButton = (
      <CCButton
        type="fill"
        onPress={handleResume}
        contentContainerStyle={styles.actionButtonContent}>
        <CCText fontSize={FontSizes[14]} fontWeight="semibold">
          Resume
        </CCText>
      </CCButton>
    );
  } else if (myState === 'pooled') {
    stateLine = (
      <Box flexDirection="row" alignItems="center" gap={6}>
        <CCText fontSize={FontSizes[14]} style={{color: lbColors.ink}}>
          Finding your next game…
        </CCText>
        <DotLoader />
      </Box>
    );
    actionButton = (
      <CCButton
        onPress={handlePause}
        style={styles.softButton}
        contentContainerStyle={styles.actionButtonContent}>
        <CCText
          fontSize={FontSizes[13]}
          fontWeight="semibold"
          style={{color: lbColors.goldInk}}>
          Pause
        </CCText>
      </CCButton>
    );
  } else {
    // idle — not paused, not pooled, not seated
    stateLine = (
      <CCText fontSize={FontSizes[14]} style={{color: lbColors.ink}}>
        {!isJoined
          ? "You're spectating — join the tournament to play."
          : notStarted
          ? 'You will be paired automatically when the arena starts.'
          : "You're not in the pairing pool."}
      </CCText>
    );
    if (!notStarted && isJoined) {
      actionButton = (
        <CCButton
          type="fill"
          onPress={handleResume}
          contentContainerStyle={styles.actionButtonContent}>
          <CCText fontSize={FontSizes[14]} fontWeight="semibold">
            Join the pool
          </CCText>
        </CCButton>
      );
    }
  }

  return (
    <Box style={styles.container}>
      {/* Countdown bar */}
      {Boolean(countdownLabel) && (
        <Box style={styles.countdownBar}>
          <MaterialCommunityIcons
            name="timer-outline"
            size={18}
            color={lbColors.goldDeep}
          />
          <CCText
            fontSize={FontSizes[14]}
            fontWeight="semibold"
            style={{marginLeft: 6, color: lbColors.ink}}
            data-testid="arena-countdown">
            {countdownLabel}
          </CCText>
        </Box>
      )}
      <Spacer spacing={vs(10)} />

      {/* State strip (light) */}
      <Box style={styles.stateStrip}>
        <Box style={{flexShrink: 1}}>{stateLine}</Box>
        {actionButton}
      </Box>

      <Spacer spacing={vs(10)} />

      {/* Dark "YOUR SCORE" panel */}
      <Box style={styles.scorePanel}>
        <Box flexDirection="row" alignItems="center" gap={16}>
          <PlayerAvatar
            initials={initialsForName(
              myEntry?.username || myName || loggedInUserId,
            )}
            color={lbColors.teal}
            size={54}
            ring={lbColors.gold}
          />
          <Box style={{flex: 1}}>
            <CCText
              fontSize={FontSizes[11]}
              fontWeight="bold"
              style={{color: lbColors.gold, letterSpacing: 0.6}}>
              {finished ? 'YOUR FINAL SCORE' : 'YOUR SCORE'}
            </CCText>
            {/* Points and rank as co-equal headline stats — rank used to be a
                small pill and is now just as prominent. */}
            <Box flexDirection="row" alignItems="center" gap={20} marginTop={4}>
              <Box>
                <Box flexDirection="row" alignItems="center" gap={8}>
                  <CCText
                    fontSize={FontSizes[36]}
                    fontWeight="bold"
                    color="#FFFFFF">
                    {myPoints}
                  </CCText>
                  {myOnFire && <FireIcon size={24} />}
                </Box>
                <CCText
                  fontSize={FontSizes[11]}
                  fontWeight="bold"
                  style={{color: lbColors.darkText, letterSpacing: 0.6}}>
                  POINTS
                </CCText>
              </Box>
              <View style={styles.statDivider} />
              <Box>
                <CCText
                  fontSize={FontSizes[30]}
                  fontWeight="bold"
                  style={{color: lbColors.gold}}>
                  {myRank ? `#${myRank}` : '—'}
                </CCText>
                <CCText
                  fontSize={FontSizes[11]}
                  fontWeight="bold"
                  style={{color: lbColors.darkText, letterSpacing: 0.6}}>
                  RANK
                </CCText>
              </Box>
            </Box>
            <CCText fontSize={FontSizes[12]} style={{color: lbColors.darkText}}>
              Streak: {myStreak}
              {myOnFire ? '  — on fire! 2× points' : ''}
            </CCText>
          </Box>
        </Box>

        {/* Recent results chips + expandable full history */}
        {Boolean(myEntry?.scores?.length) && (
          <>
            <Spacer spacing={vs(14)} />
            <View style={styles.panelDivider} />
            <Spacer spacing={vs(12)} />
            <CCText
              fontSize={FontSizes[11]}
              fontWeight="bold"
              style={{color: lbColors.gold, letterSpacing: 0.8}}>
              RECENT RESULTS
            </CCText>
            <Spacer spacing={vs(6)} />
            <ScoreChips scores={myEntry?.scores || []} />
          </>
        )}
        {/* Entry point to full history. gamesPlayed comes from myEntry, which is
            absent when the logged-in player is outside the shipped top-N — so
            also offer it to any joined player once the arena is live (the fetch
            fills it; empty otherwise), and only show a count when we know it. */}
        {(myGamesPlayed > 0 || (isJoined && !notStarted)) && (
          <>
            <Spacer spacing={vs(10)} />
            <Pressable
              onPress={toggleMyGames}
              style={styles.viewAllBtn}
              data-testid="arena-view-all-games">
              <CCText
                fontSize={FontSizes[12]}
                fontWeight="semibold"
                style={{color: lbColors.gold}}>
                {myGamesOpen
                  ? 'Hide games'
                  : myGamesPlayed > 0
                  ? `View all ${myGamesPlayed} game${
                      myGamesPlayed === 1 ? '' : 's'
                    }`
                  : 'View my games'}
              </CCText>
              <MaterialCommunityIcons
                name={myGamesOpen ? 'chevron-up' : 'chevron-down'}
                size={18}
                color={lbColors.gold}
              />
            </Pressable>
          </>
        )}
        {Boolean(status?.berserkEnabled) && !finished && (
          <>
            <Spacer spacing={vs(8)} />
            <CCText fontSize={FontSizes[11]} style={{color: lbColors.darkText}}>
              ⚡ Berserk enabled — halve your clock before your first move for a
              bonus point.
            </CCText>
          </>
        )}
      </Box>

      {/* My full game history (expanded) */}
      {myGamesOpen && (
        <ArenaPlayerGames
          games={myGamesHook.games}
          loading={myGamesHook.loading}
          disabled={Boolean(playing)}
          emptyText="No games played yet."
          onOpenGame={openGameOnBoard}
        />
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 8,
    paddingBottom: 16,
    backgroundColor: lbColors.bg,
  },
  countdownBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: lbRadius.md,
    backgroundColor: lbColors.goldSoft,
  },
  scorePanel: {
    backgroundColor: lbColors.darkPanel,
    borderRadius: lbRadius.xl,
    padding: 22,
    ...lbShadow.card,
  },
  panelDivider: {
    height: 1,
    backgroundColor: '#5A4D2E',
    opacity: 0.5,
  },
  statDivider: {
    width: 1,
    height: 34,
    backgroundColor: '#5A4D2E',
    opacity: 0.5,
  },
  viewAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
  },
  stateStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.lg,
    paddingHorizontal: 16,
    paddingVertical: 12,
    ...lbShadow.card,
  },
  // A clearly-enabled secondary action (Pause) that still stays subordinate
  // to the filled Resume button — a visible border + darker/semibold label,
  // matching the repo's outline-button idiom instead of a flat tinted fill
  // that reads as disabled.
  softButton: {
    backgroundColor: lbColors.goldSoft,
    borderWidth: 1,
    borderColor: lbColors.goldDeep,
  },
  actionButtonContent: {
    paddingHorizontal: 20,
    paddingVertical: 8,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  scoreChip: {
    width: 30,
    height: 30,
    borderRadius: 8,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default ArenaLobby;
